// ─── Parse Guardrails — Data Review detection runner ────────────────────────
// Three layers (see DESIGN_SYSTEM.md §Data Review queue):
//
//   Layer 1  Absolute thresholds — hard upper/lower bounds on derived values.
//   Layer 2  Category $/lb ratios — items outside their category band.
//   Layer 3  Cross-validation — current line ≥10× / ≤0.1× the item's history.
//
// Confidence elevation: when Layer 1 fires, the runner simulates order-of-
// magnitude corrections (÷100, ×100, ÷10, ×10) on the suspect field. If any
// correction produces values that pass all detection layers AND match the
// item's category $/lb range, confidence = 'high' and the corrected value is
// stored in suggested_*. Otherwise confidence is medium (Layer 1) or low
// (Layer 2 alone). Layer 3 with a clean OoM ratio is also high.
//
// Idempotency: the runner reads needs_review state, so re-running on the
// same data either flips needs_review false→true (new flag) or maintains
// existing state. parse_review_log entries are inserted only on flag events
// (a re-flag after a dismiss creates a new row).
//
// Dismiss is a state, not a permanent exemption. Re-running detection on
// new invoice data can re-flag a previously-dismissed item.

import { supabase } from '../supabase';

// ─── Threshold constants ────────────────────────────────────────────────────
// Documented in DESIGN_SYSTEM.md §Data Review queue. Re-evaluate quarterly.
export const ABSOLUTE_BOUNDS = {
  total_weight_lbs:  { min: 0.05,  max: 200 },
  pack_count:        { min: 1,     max: 5000 },
  piece_weight_oz:   { min: 0.01,  max: 1500 },
  price_per_lb:      { min: 0.05,  max: 200 },
  unit_price:        { min: 0.50,  max: 5000 },
};

export const CATEGORY_PERLB_BOUNDS = {
  Proteins:  { min: 0.50,  max: 25.00 },
  Produce:   { min: 0.20,  max: 15.00 },
  Dairy:     { min: 0.75,  max: 20.00 },
  'Dry Goods': { min: 0.10,  max: 30.00 },
  Beverages: { min: 0.15,  max: 20.00 },
  Seafood:   { min: 1.00,  max: 50.00 },
  Desserts:  { min: 0.50,  max: 30.00 },
  // Other: no bound — too varied
};

const CROSS_VALIDATION_RATIO_LO = 0.1;
const CROSS_VALIDATION_RATIO_HI = 10;
const MIN_HISTORY_FOR_CROSS_VAL = 3;

// ─── Helpers ────────────────────────────────────────────────────────────────

/**
 * Parse a piece_weight string like "67 OZ", "1.5#", "5 LB", "#10", "1 GAL"
 * into ounces. Returns null if not parseable.
 */
function parsePieceWeightToOz(s) {
  if (!s) return null;
  const m = String(s).match(/(\d+(?:\.\d+)?)\s*(OZ|LB|#|GAL|QT|PT)/i);
  if (!m) return null;
  const v = parseFloat(m[1]);
  const u = m[2].toUpperCase();
  if (u === 'OZ') return v;
  if (u === 'LB' || u === '#') return v * 16;
  if (u === 'GAL') return v * 128;        // fluid oz approximation; close enough for sanity bounds
  if (u === 'QT')  return v * 32;
  if (u === 'PT')  return v * 16;
  return null;
}

function median(arr) {
  if (!arr || arr.length === 0) return null;
  const sorted = arr.slice().sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 0 ? (sorted[mid - 1] + sorted[mid]) / 2 : sorted[mid];
}

function inBounds(value, bounds) {
  if (value == null || !isFinite(value)) return true; // null = not applicable
  return value >= bounds.min && value <= bounds.max;
}

// ─── Layer evaluators ───────────────────────────────────────────────────────

/**
 * Layer 1: returns { fail: bool, reasons: [{field, value, bound}] }.
 * Evaluates against a "candidate" — could be the canonical food_items row OR
 * a hypothetical post-correction snapshot.
 */
function checkLayer1(candidate) {
  const reasons = [];
  const checks = [
    ['total_weight_lbs', candidate.total_weight_lbs ?? candidate.weight_per_case],
    ['pack_count', candidate.pack_count],
    ['piece_weight_oz', candidate.piece_weight_oz],
    ['price_per_lb', candidate.price_per_lb],
    ['unit_price', candidate.unit_price ?? candidate.current_price],
  ];
  for (const [field, value] of checks) {
    if (value == null) continue;
    const b = ABSOLUTE_BOUNDS[field];
    if (!b) continue;
    if (!inBounds(Number(value), b)) {
      reasons.push({ field, value: Number(value), bound: b });
    }
  }
  return { fail: reasons.length > 0, reasons };
}

/**
 * Layer 2: category $/lb mismatch. Skips if category is null or 'Other'.
 */
function checkLayer2(candidate, category) {
  if (!category || category === 'Other') return { fail: false };
  const b = CATEGORY_PERLB_BOUNDS[category];
  if (!b) return { fail: false };
  const v = candidate.price_per_lb;
  if (v == null) return { fail: false };
  if (!inBounds(Number(v), b)) {
    return { fail: true, reasons: [{ field: 'price_per_lb', value: Number(v), bound: b, category }] };
  }
  return { fail: false };
}

/**
 * Layer 3: cross-validation against the item's prior $/lb history.
 * Returns { fail, ratio, hist_perlb, recent_perlb } or { fail: false }.
 */
function checkLayer3(linesForItem) {
  const eligible = (linesForItem || []).filter(l => l.price_per_lb != null);
  if (eligible.length < MIN_HISTORY_FOR_CROSS_VAL + 1) return { fail: false };
  // Lines pre-sorted DESC by invoice_date. Most recent = recent[0].
  const recent = eligible[0];
  const hist = eligible.slice(1);
  const histMedian = median(hist.map(l => Number(l.price_per_lb)));
  if (!histMedian) return { fail: false };
  const ratio = Number(recent.price_per_lb) / histMedian;
  if (ratio <= CROSS_VALIDATION_RATIO_LO || ratio >= CROSS_VALIDATION_RATIO_HI) {
    return {
      fail: true,
      ratio,
      hist_perlb: histMedian,
      recent_perlb: Number(recent.price_per_lb),
      recent_line_id: recent.id,
    };
  }
  return { fail: false };
}

// ─── Confidence elevation: simulated OoM corrections ────────────────────────

/**
 * Try OoM corrections on a single field. Returns the first correction that
 * produces a snapshot passing both Layer 1 (absolute) AND Layer 2 (category
 * $/lb), OR null if none cleans up.
 *
 * Simulation order per spec (most-common errors first):
 *   piece_weight ÷100, ×100, ÷10, ×10, then pack_count, then total_weight.
 *
 * For each correction, we recompute total_weight_lbs and price_per_lb from
 * the corrected pack data + the trusted case_price, then evaluate.
 */
function attemptCorrection(item) {
  const casePrice = Number(item.unit_price ?? item.current_price ?? 0);
  if (!casePrice || casePrice <= 0) return null;

  const baseCandidates = [];
  const factors = [0.01, 100, 0.1, 10];

  // 1. piece_weight corrections
  if (item.piece_weight_oz != null) {
    for (const f of factors) {
      const newOz = item.piece_weight_oz * f;
      if (newOz <= 0) continue;
      const newPack = item.pack_count;
      const newTotalLbs = newPack ? (newPack * newOz) / 16 : null;
      const newPerLb = newTotalLbs ? casePrice / newTotalLbs : null;
      baseCandidates.push({
        kind: 'piece_weight_oz',
        factor: f,
        suggested_piece_weight_oz: newOz,
        suggested_pack_count: newPack,
        suggested_total_weight_lbs: newTotalLbs,
        suggested_price_per_lb: newPerLb,
      });
    }
  }

  // 2. pack_count corrections
  if (item.pack_count != null) {
    for (const f of [0.01, 100, 0.1, 10]) {
      const newPack = item.pack_count * f;
      if (newPack <= 0) continue;
      const oz = item.piece_weight_oz;
      const newTotalLbs = oz ? (newPack * oz) / 16 : null;
      const newPerLb = newTotalLbs ? casePrice / newTotalLbs : null;
      baseCandidates.push({
        kind: 'pack_count',
        factor: f,
        suggested_pack_count: newPack,
        suggested_piece_weight_oz: oz,
        suggested_total_weight_lbs: newTotalLbs,
        suggested_price_per_lb: newPerLb,
      });
    }
  }

  // 3. total_weight_lbs corrections (when pack/piece are absent)
  const baseWeight = item.total_weight_lbs ?? item.weight_per_case;
  if (baseWeight != null) {
    for (const f of [0.01, 100, 0.1, 10]) {
      const newWeight = baseWeight * f;
      if (newWeight <= 0) continue;
      const newPerLb = casePrice / newWeight;
      baseCandidates.push({
        kind: 'total_weight_lbs',
        factor: f,
        suggested_total_weight_lbs: newWeight,
        suggested_price_per_lb: newPerLb,
        suggested_pack_count: item.pack_count,
        suggested_piece_weight_oz: item.piece_weight_oz,
      });
    }
  }

  // Evaluate each candidate against Layer 1 + Layer 2.
  for (const c of baseCandidates) {
    const snapshot = {
      total_weight_lbs: c.suggested_total_weight_lbs,
      pack_count: c.suggested_pack_count,
      piece_weight_oz: c.suggested_piece_weight_oz,
      price_per_lb: c.suggested_price_per_lb,
      unit_price: casePrice,
    };
    if (checkLayer1(snapshot).fail) continue;
    if (checkLayer2(snapshot, item.category).fail) continue;
    return c;
  }

  return null;
}

// ─── Operator-facing description helpers ────────────────────────────────────
// Used by both the Data Review section card and the inline expandable chip
// on item rows. Single source of truth for the human-readable problem
// language across surfaces.

/**
 * describeNeedsReview(item) → { problem, trace }
 *   problem: plain-language one-liner ("Case weight reads 1,001 lbs — likely
 *            OCR error.")
 *   trace:   the technical detection_reason line for the small italic sub-
 *            text under the problem ("Detected from: $/lb is below $0.05/lb
 *            threshold")
 *
 * Returns null if the item isn't flagged.
 */
export function describeNeedsReview(item) {
  if (!item || !item.needs_review) return null;
  const reason = item.review_reason || '';
  let problem = 'This item flagged for review.';

  // Build a friendly number formatter inline (this module deliberately has no
  // import-time dependency on a UI helper).
  const fmt = (n) => {
    if (n == null || !isFinite(n)) return '\u2014';
    const v = Number(n);
    if (Math.abs(v) >= 1000) return v.toLocaleString('en-US', { maximumFractionDigits: 0 });
    if (Math.abs(v) >= 10)   return v.toLocaleString('en-US', { maximumFractionDigits: 1 });
    return v.toLocaleString('en-US', { maximumFractionDigits: 2 });
  };

  if (reason.includes('total_weight_lbs')) {
    problem = `Case weight reads ${fmt(item.weight_per_case)} lbs — likely OCR error.`;
  } else if (reason.includes('price_per_lb')) {
    problem = `Price/lb reads $${fmt(item.price_per_lb)}/lb — outside expected range.`;
  } else if (reason.includes('pack_count')) {
    problem = `Pack count looks implausible.`;
  } else if (reason.includes('piece_weight')) {
    problem = `Piece weight is suspect — likely OCR error.`;
  } else if (reason.includes('unit_price')) {
    problem = `Case price reads $${fmt(item.current_price)} — outside expected range.`;
  }

  return {
    problem,
    trace: `Detected from: ${reason}`,
    confidence: item.review_confidence || 'medium',
  };
}

// ─── isItemUsable — exposed helper for downstream calcs ─────────────────────

/**
 * Returns false for items that should be excluded from Volume Tracker /
 * Inventory Trends totals. Does NOT exclude from price tracking — price is
 * trusted; only pack/weight is suspect.
 */
export function isItemUsable(item) {
  if (!item) return false;
  return !item.needs_review;
}

// ─── Main runner ────────────────────────────────────────────────────────────

/**
 * Run guardrails against a company's items.
 *
 * @param {object} opts
 * @param {number|string} opts.companyId           — required
 * @param {Array<number>} [opts.foodItemIds]       — limit detection to these items.
 *                                                    If omitted, evaluates all items
 *                                                    for the company.
 * @param {boolean} [opts.dryRun=false]            — when true, computes but does not
 *                                                    write needs_review or insert log
 *                                                    rows. Returns the would-fire list.
 * @returns {Promise<{ checked: number, flagged: number, by_layer: object,
 *                     wouldFire: Array }>}
 */
export async function runParseGuardrails({ companyId, foodItemIds, dryRun = false }) {
  if (!companyId) throw new Error('runParseGuardrails: companyId required');

  // 1. Load items + last-90d invoice lines.
  let itemsQuery = supabase.from('food_items').select('*').eq('company_id', companyId);
  if (foodItemIds && foodItemIds.length > 0) itemsQuery = itemsQuery.in('id', foodItemIds);
  const { data: items, error: itemsErr } = await itemsQuery;
  if (itemsErr) throw itemsErr;
  if (!items || items.length === 0) {
    return { checked: 0, flagged: 0, by_layer: {}, wouldFire: [] };
  }

  const ids = items.map(i => i.id);
  const ninetyDaysAgo = new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
  const { data: lines, error: linesErr } = await supabase
    .from('invoice_line_items')
    .select('id, food_item_id, invoice_date, pack_count, piece_weight, total_weight, price_per_lb, unit_price')
    .eq('company_id', companyId)
    .in('food_item_id', ids)
    .gte('invoice_date', ninetyDaysAgo)
    .order('invoice_date', { ascending: false })
    .order('id', { ascending: false });
  if (linesErr) throw linesErr;

  const linesByItem = {};
  (lines || []).forEach(l => {
    if (!linesByItem[l.food_item_id]) linesByItem[l.food_item_id] = [];
    linesByItem[l.food_item_id].push(l);
  });

  const byLayer = { absolute_threshold: 0, category_ratio: 0, cross_validation: 0 };
  const wouldFire = [];
  let flaggedCount = 0;

  // 2. Per-item evaluation.
  for (const item of items) {
    const itemLines = linesByItem[item.id] || [];

    // Build a "candidate" snapshot from canonical item + most-recent line's
    // pack data (which usually mirrors the canonical pack_size).
    const recentLine = itemLines[0] || null;
    const piece_oz = parsePieceWeightToOz(recentLine?.piece_weight);
    const candidate = {
      pack_count: recentLine?.pack_count ?? null,
      piece_weight_oz: piece_oz,
      total_weight_lbs: recentLine?.total_weight ?? Number(item.weight_per_case) ?? null,
      price_per_lb: Number(item.price_per_lb) || (recentLine?.price_per_lb != null ? Number(recentLine.price_per_lb) : null),
      unit_price: Number(item.current_price) || (recentLine?.unit_price != null ? Number(recentLine.unit_price) : null),
    };

    const l1 = checkLayer1(candidate);
    const l2 = checkLayer2(candidate, item.category);
    const l3 = checkLayer3(itemLines);

    if (!l1.fail && !l2.fail && !l3.fail) {
      // Item is clean. If it was previously flagged AND still in DB as flagged,
      // we leave needs_review alone — only the "Accept fix" / "Edit manually" /
      // "Dismiss" flows clear it. The runner doesn't auto-clear flags.
      continue;
    }

    flaggedCount++;

    // Pick the dominant detection layer for the parse_review_log entry.
    let detection_layer, detection_reason, flagged_field, flagged_value;
    if (l1.fail) {
      detection_layer = 'absolute_threshold';
      const r = l1.reasons[0];
      detection_reason = `${r.field}_out_of_bounds (${r.value} outside ${r.bound.min}–${r.bound.max})`;
      flagged_field = r.field; flagged_value = r.value;
      byLayer.absolute_threshold++;
    } else if (l2.fail) {
      detection_layer = 'category_ratio';
      const r = l2.reasons[0];
      detection_reason = `${r.category}_perlb_out_of_band ($${r.value.toFixed(2)}/lb outside $${r.bound.min}–$${r.bound.max})`;
      flagged_field = 'price_per_lb'; flagged_value = r.value;
      byLayer.category_ratio++;
    } else if (l3.fail) {
      detection_layer = 'cross_validation';
      detection_reason = `perlb_ratio_${l3.ratio.toFixed(3)} (recent $${l3.recent_perlb.toFixed(2)}/lb vs hist median $${l3.hist_perlb.toFixed(2)}/lb)`;
      flagged_field = 'price_per_lb'; flagged_value = l3.recent_perlb;
      byLayer.cross_validation++;
    }

    // Confidence elevation. Try OoM correction first; if a clean correction
    // exists that passes Layer 1 + 2, confidence = high. Otherwise fall back
    // to layer-based confidence.
    const correction = attemptCorrection(candidate);
    let confidence, suggestion;
    if (correction) {
      confidence = 'high';
      suggestion = correction;
    } else if (l1.fail) {
      confidence = 'medium';
    } else if (l2.fail) {
      confidence = 'low';
    } else if (l3.fail) {
      // Cross-validation alone with no clean OoM is rare, but possible.
      confidence = 'medium';
    }

    const row = {
      food_item_id: item.id,
      item_name: item.normalized_name,
      detection_layer,
      detection_reason,
      flagged_field,
      flagged_value,
      confidence,
      suggestion,
    };
    wouldFire.push(row);

    if (dryRun) continue;

    // 3. Persist: update food_items + insert parse_review_log row.
    const updates = {
      needs_review: true,
      review_reason: detection_reason,
      review_confidence: confidence,
    };
    if (suggestion) {
      if (suggestion.suggested_pack_count       != null) updates.suggested_pack_count       = Math.round(Number(suggestion.suggested_pack_count));
      if (suggestion.suggested_piece_weight_oz  != null) updates.suggested_piece_weight_oz  = Number(suggestion.suggested_piece_weight_oz);
      if (suggestion.suggested_total_weight_lbs != null) updates.suggested_total_weight_lbs = Number(suggestion.suggested_total_weight_lbs);
      if (suggestion.suggested_price_per_lb     != null) updates.suggested_price_per_lb     = Number(suggestion.suggested_price_per_lb);
    } else {
      updates.suggested_pack_count = null;
      updates.suggested_piece_weight_oz = null;
      updates.suggested_total_weight_lbs = null;
      updates.suggested_price_per_lb = null;
    }
    await supabase.from('food_items').update(updates).eq('id', item.id);

    // Idempotency: only insert a log row if there isn't already an unresolved
    // log for the same (item, layer, reason). Keeps re-runs from duplicating.
    const { data: existing } = await supabase
      .from('parse_review_log')
      .select('id')
      .eq('food_item_id', item.id)
      .eq('detection_layer', detection_layer)
      .eq('detection_reason', detection_reason)
      .is('resolution', null)
      .limit(1);
    if (!existing || existing.length === 0) {
      await supabase.from('parse_review_log').insert({
        company_id: companyId,
        food_item_id: item.id,
        invoice_line_item_id: l3.fail ? l3.recent_line_id : (recentLine?.id ?? null),
        detection_layer,
        detection_reason,
        flagged_field,
        flagged_value,
        suggested_value: suggestion?.suggested_total_weight_lbs ?? suggestion?.suggested_price_per_lb ?? null,
        confidence,
      });
    }
  }

  return {
    checked: items.length,
    flagged: flaggedCount,
    by_layer: byLayer,
    wouldFire,
  };
}

// ─── Accept-fix flow helper ─────────────────────────────────────────────────

/**
 * Accept a high-confidence suggestion. Updates food_items canonical AND any
 * invoice_line_items that share the same wrong piece_weight pattern (so future
 * imports don't re-flag the same OCR error already known-bad in history).
 *
 * Rule: correct line items whose piece_weight matches the canonical's
 * (now-known-wrong) piece_weight. Lines with different piece_weight values
 * are left alone — they were either parsed correctly or have their own
 * different issue.
 */
export async function acceptParseSuggestion({ companyId, foodItemId, userEmail }) {
  // Load the item with its suggested_* values.
  const { data: item } = await supabase
    .from('food_items')
    .select('*')
    .eq('id', foodItemId)
    .maybeSingle();
  if (!item || !item.needs_review) return { ok: false, reason: 'not_flagged' };
  if (item.review_confidence !== 'high') return { ok: false, reason: 'not_high_confidence' };

  const nowIso = new Date().toISOString();

  // Build the correction payload.
  const itemUpdates = {
    needs_review: false,
    review_reason: null,
    review_confidence: null,
    suggested_pack_count: null,
    suggested_piece_weight_oz: null,
    suggested_total_weight_lbs: null,
    suggested_price_per_lb: null,
    review_resolved_at: nowIso,
    review_resolved_by: userEmail || null,
  };
  if (item.suggested_total_weight_lbs != null) itemUpdates.weight_per_case = Number(item.suggested_total_weight_lbs);
  if (item.suggested_price_per_lb     != null) itemUpdates.price_per_lb    = Number(item.suggested_price_per_lb);

  // Reconstruct the corrected piece_weight string when applicable. We keep the
  // canonical pack_size in the same "P×W" format.
  if (item.suggested_piece_weight_oz != null && item.suggested_pack_count != null) {
    const pieceLbs = item.suggested_piece_weight_oz / 16;
    const pieceStr = item.suggested_piece_weight_oz < 16
      ? `${item.suggested_piece_weight_oz} OZ`
      : `${pieceLbs}#`;
    itemUpdates.pack_size = `${item.suggested_pack_count}×${pieceStr}`;
  }

  await supabase.from('food_items').update(itemUpdates).eq('id', foodItemId);

  // Update invoice_line_items whose piece_weight matches the canonical's
  // OLD (wrong) pack pattern. We use the existing pack_size as the matching
  // pattern. Because we just updated pack_size, we need to read it BEFORE
  // updating — so capture original pack_size before the food_items update
  // above. (Refetched here to avoid race).
  // For corn-dog-class fixes the wrong pattern was "67 OZ"; corrected is
  // "0.67 OZ". We match on the wrong piece_weight.
  if (item.suggested_piece_weight_oz != null) {
    const wrongPiece = parsePieceWeightToOzInverse(item.pack_size);
    if (wrongPiece) {
      const correctedPieceStr = item.suggested_piece_weight_oz < 16
        ? `${item.suggested_piece_weight_oz} OZ`
        : `${item.suggested_piece_weight_oz / 16}#`;
      const correctedTotal = (item.suggested_pack_count ?? 0) * (item.suggested_piece_weight_oz ?? 0) / 16;
      const newPerLb = correctedTotal > 0 ? Number(item.suggested_price_per_lb) || null : null;

      // Pull lines whose pack pattern matches the wrong one.
      const { data: lines } = await supabase
        .from('invoice_line_items')
        .select('id, piece_weight, unit_price, quantity')
        .eq('company_id', companyId)
        .eq('food_item_id', foodItemId)
        .eq('piece_weight', wrongPiece);

      for (const l of (lines || [])) {
        await supabase.from('invoice_line_items').update({
          piece_weight: correctedPieceStr,
          total_weight: correctedTotal || null,
          price_per_lb: newPerLb,
          needs_review: false,
        }).eq('id', l.id);
      }
    }
  }

  // Log the resolution.
  await supabase.from('parse_review_log').update({
    resolution: 'accepted_suggestion',
    resolved_at: nowIso,
    resolved_by: userEmail || null,
  }).eq('food_item_id', foodItemId).is('resolution', null);

  // Re-run detection to confirm clean. If new violations exist, the runner
  // re-flags. For corn dog this should produce { checked: 1, flagged: 0 }.
  const recheck = await runParseGuardrails({ companyId, foodItemIds: [foodItemId] });

  return { ok: true, recheck };
}

// Tiny helper: extract the piece_weight portion of a pack_size string
// ("239×67 OZ" → "67 OZ"). Used to match line items to the wrong pattern.
function parsePieceWeightToOzInverse(packSize) {
  if (!packSize) return null;
  const idx = String(packSize).indexOf('\u00D7'); // multiplication sign
  if (idx < 0) {
    // fallback to "x" or "X"
    const m = String(packSize).match(/\d+\s*[xX]\s*(.+)/);
    return m ? m[1].trim() : null;
  }
  return String(packSize).slice(idx + 1).trim();
}

// ─── Dismiss flow helper ────────────────────────────────────────────────────
export async function dismissParseFlag({ foodItemId, userEmail, reason }) {
  const nowIso = new Date().toISOString();
  await supabase.from('food_items').update({
    needs_review: false,
    review_reason: null,
    review_confidence: null,
    suggested_pack_count: null,
    suggested_piece_weight_oz: null,
    suggested_total_weight_lbs: null,
    suggested_price_per_lb: null,
    review_resolved_at: nowIso,
    review_resolved_by: userEmail || null,
  }).eq('id', foodItemId);

  await supabase.from('parse_review_log').update({
    resolution: 'dismissed',
    resolved_at: nowIso,
    resolved_by: userEmail || null,
    resolution_notes: reason || null,
  }).eq('food_item_id', foodItemId).is('resolution', null);
}
